<script lang="ts">
	import { Card, Button, Badge, Heading } from 'flowbite-svelte';
	import { push } from '../../svelte-spa-router';
	import { configurationStore } from '../../AppConfiguration';
	import AbstractID from '../../AbstractID';
	import site from '../../site';
	import { onMount } from 'svelte';
	import type { User, AuditLog } from '../../AppTypes';
	import { exportConfig, importConfig } from '../../AppNetwork';

	let transferBusy = false;
	let transferMsg = '';
	let transferErr = '';
	let fileInput: HTMLInputElement;

	async function doExport() {
		transferBusy = true; transferMsg = ''; transferErr = '';
		try {
			const data = await exportConfig();
			const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			const stamp = new Date().toISOString().slice(0, 10);
			a.href = url;
			a.download = `${site.shortname || site.name}-config-${stamp}.json`;
			a.click();
			URL.revokeObjectURL(url);
			transferMsg = 'Configuration exported.';
		} catch (e) {
			transferErr = e instanceof Error ? e.message : 'Export failed';
		} finally {
			transferBusy = false;
		}
	}

	async function onImportFile(event: Event) {
		const input = event.currentTarget as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;
		transferBusy = true; transferMsg = ''; transferErr = '';
		try {
			const data = JSON.parse(await file.text());
			const s = await importConfig(data);
			transferMsg = `Imported — groups +${s.groups.created}/~${s.groups.updated}, probes +${s.probes.created}/~${s.probes.updated}, services +${s.services.created}/~${s.services.updated}.`;
		} catch (e) {
			transferErr = e instanceof Error ? e.message : 'Import failed (is the file a valid export?)';
		} finally {
			transferBusy = false;
			input.value = '';
		}
	}

	let stats = {
		totalUsers: 0,
		adminUsers: 0,
		recentLogins: 0,
		auditEntries: 0
	};
	let loading = true;
	let error = '';

	onMount(async () => {
		await loadStats();
	});

	async function loadStats() {
		loading = true;
		error = '';

		try {
			// Load user count
			const usersResponse = await AbstractID.authenticatedFetch(site.backend.api.userList);
			if (usersResponse.ok) {
				const usersData = await usersResponse.json();
				stats.totalUsers = usersData.total || 0;
				stats.adminUsers = usersData.items?.filter((u: User) => u.isAdmin).length || 0;
			}

			// Load recent audit entries count
			const auditResponse = await AbstractID.authenticatedFetch(site.backend.api.auditList);
			if (auditResponse.ok) {
				const auditData = await auditResponse.json();
				stats.auditEntries = auditData.total || 0;
				
				// Count recent logins (last 24 hours)
				const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
				stats.recentLogins = auditData.items?.filter((e: AuditLog) => 
					e.action === 'USER_LOGIN' && new Date(e.creationDate) > oneDayAgo
				).length || 0;
			}
		} catch (err) {
			error = 'Failed to load admin statistics';
			console.error('Admin stats error:', err);
		} finally {
			loading = false;
		}
	}

	function navigateToUsers() {
		push('/Admin/Users');
	}

	function navigateToLogs() {
		push('/Admin/Settings/Admin/Logs');
	}
</script>

<Heading tag="h3">Administration</Heading>

<div class="space-y-6">
	<!-- Quick Stats -->
	<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
		<Card padding="sm" class="max-w-none">
			<div class="flex items-center gap-3">
				<div class="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
					<i class="fa-solid fa-users text-blue-600 dark:text-blue-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.totalUsers}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">Total Users</p>
				</div>
			</div>
		</Card>

		<Card padding="sm" class="max-w-none">
			<div class="flex items-center gap-3">
				<div class="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
					<i class="fa-solid fa-shield text-yellow-600 dark:text-yellow-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.adminUsers}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">Administrators</p>
				</div>
			</div>
		</Card>

		<Card padding="sm" class="max-w-none">
			<div class="flex items-center gap-3">
				<div class="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
					<i class="fa-solid fa-right-to-bracket text-green-600 dark:text-green-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.recentLogins}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">Logins (24h)</p>
				</div>
			</div>
		</Card>

		<Card padding="sm" class="max-w-none">
			<div class="flex items-center gap-3">
				<div class="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
					<i class="fa-solid fa-clipboard-list text-purple-600 dark:text-purple-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.auditEntries}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">Audit Entries</p>
				</div>
			</div>
		</Card>
	</div>

	{#if error}
		<div class="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>
			{error}
			<Button size="xs" color="red" class="ms-4" on:click={loadStats}>
				Retry
			</Button>
		</div>
	{/if}

	<!-- Quick Actions -->
	<Card class="max-w-none">
		<h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
			<i class="fa-solid fa-bolt me-2"></i>
			Quick Actions
		</h3>
		
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
			<button
				on:click={navigateToUsers}
				class="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
			>
				<div class="flex items-center gap-3 mb-2">
					<i class="fa-solid fa-user-gear text-primary-600 dark:text-primary-400"></i>
					<span class="font-medium text-gray-900 dark:text-white">Manage Users</span>
				</div>
				<p class="text-sm text-gray-500 dark:text-gray-400">
					View, search, and manage user accounts
				</p>
			</button>

			<button
				on:click={navigateToLogs}
				class="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
			>
				<div class="flex items-center gap-3 mb-2">
					<i class="fa-solid fa-scroll text-primary-600 dark:text-primary-400"></i>
					<span class="font-medium text-gray-900 dark:text-white">Audit Logs</span>
				</div>
				<p class="text-sm text-gray-500 dark:text-gray-400">
					Review system activity and security events
				</p>
			</button>

			<button
				on:click={loadStats}
				class="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
			>
				<div class="flex items-center gap-3 mb-2">
					<i class="fa-solid fa-refresh text-primary-600 dark:text-primary-400"></i>
					<span class="font-medium text-gray-900 dark:text-white">Refresh Stats</span>
				</div>
				<p class="text-sm text-gray-500 dark:text-gray-400">
					Reload all administration statistics
				</p>
			</button>
		</div>
	</Card>

	<!-- Configuration backup -->
	<Card class="max-w-none">
		<h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
			<i class="fa-solid fa-box-archive me-2"></i>
			Configuration backup
		</h3>
		<p class="text-sm text-gray-500 dark:text-gray-400">
			Export all probes, services and groups to a JSON file, or import one to restore or clone them.
			Re-importing updates existing items (matched by slug/name) and never deletes anything.
			<span class="text-amber-600 dark:text-amber-400">The file contains probe secret keys — store it securely.</span>
		</p>

		<div class="flex flex-wrap gap-3 mt-4">
			<Button on:click={doExport} disabled={transferBusy}>
				<i class="fa-solid fa-download me-2"></i>Export
			</Button>
			<Button color="alternative" on:click={() => fileInput.click()} disabled={transferBusy}>
				<i class="fa-solid fa-upload me-2"></i>Import
			</Button>
			<input
				type="file"
				accept="application/json,.json"
				class="hidden"
				bind:this={fileInput}
				on:change={onImportFile}
			/>
		</div>

		{#if transferMsg}<p class="mt-3 text-sm text-green-600 dark:text-green-400">{transferMsg}</p>{/if}
		{#if transferErr}<p class="mt-3 text-sm text-red-500">{transferErr}</p>{/if}
	</Card>

	<!-- System Information -->
	<Card class="max-w-none">
		<h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
			<i class="fa-solid fa-info-circle me-2"></i>
			System Information
		</h3>
		
		<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div class="space-y-3">
				<div class="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">Application</span>
					<span class="text-gray-900 dark:text-white">{$configurationStore?.name || 'AbstractStatus'}</span>
				</div>
				<div class="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">Environment</span>
					<Badge color="yellow">
						N/A
					</Badge>
				</div>
				<div class="flex justify-between py-2">
					<span class="text-gray-600 dark:text-gray-400">Version</span>
					<span class="text-gray-900 dark:text-white">{$configurationStore?.version || '1.0.0'}</span>
				</div>
			</div>
			
			<div class="space-y-3">
				<div class="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">Auth Provider</span>
					<span class="text-gray-900 dark:text-white">AbstractID</span>
				</div>
				<div class="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">Database</span>
					<span class="text-gray-900 dark:text-white">PostgreSQL</span>
				</div>
				<div class="flex justify-between py-2">
					<span class="text-gray-600 dark:text-gray-400">Framework</span>
					<span class="text-gray-900 dark:text-white">Svelte 4 + Express</span>
				</div>
			</div>
		</div>
	</Card>
</div>
